/* "Delete this review" — the way out.
 *
 * Ceiling's whole claim is that a document a federal reviewer opens in it never
 * leaves their browser. That claim is only half kept if there is no way to take
 * it out of their browser either, so this is a first-class control rather than
 * a footnote: it is on the review itself, and on the list of reviews on /new.
 *
 * Two steps, never one. The confirmation says exactly what goes — including the
 * page text Ceiling extracted, which is the part a reviewer would not think of
 * and the part most worth naming — and exactly where it goes from, which is
 * this browser and nowhere else, because there is nowhere else.
 *
 * `store.deleteReview()` is what actually removes it; tools/feedback-check.tsx
 * enumerates the store afterwards and proves nothing is left. */

import { useState } from 'react';

import { Button } from '../primitives';
import './DeleteReview.css';

export interface DeleteReviewProps {
  /**
   * True when the document itself is in this browser. False for the worked
   * example, which ships with the application: there the only thing stored is
   * the reviewer's own decisions, and saying otherwise would be untrue.
   */
  stored?: boolean;
  onDelete(): void;
  /** Set while the deletion is running, so it cannot be asked for twice. */
  busy?: boolean;
  /** The layout this sits in. 'row' is the compact form used by /new's list. */
  variant?: 'block' | 'row';
}

/** What the confirmation says goes. Exported so it is asserted, not trusted. */
export function deleteWarning(stored: boolean): string {
  return stored
    ? 'This removes the document, the page text Ceiling extracted from it, every finding, and every decision, note and comment you made — from this browser, which is the only place any of it exists. It cannot be undone.'
    : 'This removes every decision, note and comment you made on the example — from this browser, which is the only place they exist. The example plan itself ships with the application and stays. It cannot be undone.';
}

export default function DeleteReview({
  stored = true,
  onDelete,
  busy = false,
  variant = 'block',
}: DeleteReviewProps) {
  const [confirming, setConfirming] = useState(false);

  if (!confirming) {
    return (
      <div className={`dr dr--${variant}`}>
        <Button variant="ghost" onClick={() => setConfirming(true)}>
          Delete this review
        </Button>
        {variant === 'block' ? (
          <p className="dr__hint">
            Removes it from this browser completely. Nothing about it exists
            anywhere else.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={`dr dr--${variant} dr--confirming`}
      role="group"
      aria-label="Delete this review"
    >
      <p className="dr__question">Delete this review?</p>
      <p className="dr__warning">{deleteWarning(stored)}</p>
      <div className="dr__row">
        <Button variant="primary" marker disabled={busy} onClick={onDelete}>
          {busy ? 'Deleting…' : 'Delete it'}
        </Button>
        <Button
          variant="secondary"
          disabled={busy}
          onClick={() => setConfirming(false)}
        >
          Keep it
        </Button>
      </div>
    </div>
  );
}
